import {
    Fragment,
    memo,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import type { FC, ReactNode } from "react";
import useSWR from "swr";
import { fetchTokenTXS } from "~/api/endpoints";
import { getSafeAreaInsetBottom } from "~/components";
import { useSnackbar } from "~/Provider/Snackbar";
import "./FooterTxsPopView.css";
import {
    hapticFeedback,
    themeParams,
    useSignal,
} from "@telegram-apps/sdk-react";
import { formatValue } from "~/utils/number-format";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import randomIcon from "~/components/RandomIcon";
import { Avatar } from "@telegram-apps/telegram-ui";

interface IProps {
    children?: ReactNode;
    chain_id: string;
    contract: string;
}

const randomstring = () => {
    const part = (parseInt(String(Math.random() * 100)) + Date.now()).toString(
        16
    );
    return `0x${part}${part}`;
};

const FooterTxsPopView: FC<IProps> = ({ chain_id, contract }) => {
    const isDark = useSignal(themeParams.isDark);
    const snackbar = useSnackbar();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [randomUserAddress, setRandomUserAddress] = useState(randomstring());
    const { t } = useTranslation();

    const queryPriceImpact = useCallback(
        async ([, chainId, contract]: [string, string, string]) => {
            if (!chainId || !contract) return [];
            return fetchTokenTXS(String(chainId), contract, "3").then(
                (res) => res.data.list || []
            );
        },
        []
    );

    const hapticRef = useRef(false);
    const { data: displayData } = useSWR(
        ["TradePage:queryTokenTXS", chain_id, contract, 3],
        queryPriceImpact,
        {
            refreshInterval: 20000,
            fallbackData: [],
            onSuccess(data) {
                if (data.length > 0 && !hapticRef.current) {
                    hapticFeedback.impactOccurred("heavy");
                    hapticRef.current = true;
                }
            },
            onError() {
                snackbar.error(
                    `Can not load token info! Check your network connection!`
                );
            },
        }
    );

    useEffect(() => {
        const interval = setInterval(() => {
            setIsVisible(false);
            setTimeout(() => {
                setRandomUserAddress(randomstring());
                setCurrentIndex((prevIndex) => prevIndex % displayData.length);
                setIsVisible(true);
                hapticFeedback.impactOccurred("heavy");
            }, 4000); // 3s pause
        }, 6000); // 5s display + 3s pause

        return () => clearInterval(interval);
    }, [displayData]);

    if (displayData.length === 0) {
        return null;
    }

    const currentItem = displayData[currentIndex];

    return (
        <Fragment>
            <div className="h-[52px]" />
            <div
                style={{
                    bottom: `calc(${getSafeAreaInsetBottom()} + 3.375rem)`,
                    background: isDark
                        ? "linear-gradient(180deg, rgba(33, 33, 33, 0) 0%, rgba(33, 33, 33, 0.9) 50.5%, #212121 100%)"
                        : "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.9) 50.5%, #FFFFFF 100%)",
                    width: "100%",
                }}
                className="flex items-center justify-center h-[52px] fixed"
            >
                {currentItem && (
                    <div
                        className={`fade ${isVisible ? "fade-in" : "fade-out"} flex space-x-1 items-center top-3 bg-transparent`}
                    >
                        <Avatar
                            size={20}
                            src={randomIcon(randomUserAddress)}
                            className="scale-90"
                        />
                        <div className="text-body4 font-semibold text-text_1">
                            {currentItem.type == 1 ? t("Bought") : t("Sold")}
                        </div>
                        <div
                            className={clsx(
                                "text-body4 font-semibold text-text_1",
                                currentItem.type == 1
                                    ? "text-text_buy"
                                    : "text-text_sell"
                            )}
                        >
                            {formatValue(currentItem.value)}
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

export default memo(FooterTxsPopView);
